/**
 * useImageGeneration Hook
 *
 * API Integration for Vertex AI Image Editing
 * Handles all 4 editing modes: Inpaint Add/Remove, Outpaint, Background Edit
 */

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api-client';
import type { Image } from '@/types';

interface GenerateParams {
  image: File;
  mask: Blob;
  prompt: string;
  numberOfImages?: number;
  negativePrompt?: string;
  ratio?: string;
  resolution?: string;
}

interface UseImageGenerationReturn {
  generateInpaintAdd: (params: GenerateParams) => Promise<Image[]>;
  generateInpaintRemove: (params: GenerateParams) => Promise<Image[]>;
  generateOutpaint: (params: GenerateParams) => Promise<Image[]>;
  generateBackgroundEdit: (params: GenerateParams) => Promise<Image[]>;
  isLoading: boolean;
  error: string | null;
}

export function useImageGeneration(): UseImageGenerationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const generateRequest = useCallback(
    async (endpoint: string, params: GenerateParams): Promise<Image[]> => {
      setIsLoading(true);
      setError(null);

      try {
        const formData = new FormData();
        formData.append('image', params.image);
        formData.append('mask', params.mask);
        formData.append('prompt', params.prompt);
        if (params.numberOfImages) {
          formData.append('numberOfImages', params.numberOfImages.toString());
        }
        if (params.negativePrompt) {
          formData.append('negativePrompt', params.negativePrompt);
        }
        if (params.ratio) {
          formData.append('ratio', params.ratio);
        }
        if (params.resolution) {
          formData.append('resolution', params.resolution);
        }

        const { data } = await apiClient.post<{
          success: boolean;
          error?: string;
          images: { image_id: string; image_url: string }[];
        }>(endpoint, formData);

        if (!data.success) {
          throw new Error(data.error || 'Generation failed');
        }

        // Convert response to Image[] format (Backend liefert Minimal-Set; restliche
        // Pflichtfelder werden vom konsumierenden Hook nachgereichert.)
        const images: Image[] = data.images.map((img: { image_id: string; image_url: string }) => ({
          id: img.image_id,
          asset_id: img.image_id,
          image_url: img.image_url,
          created_at: new Date().toISOString(),
        })) as Image[];

        toast.success(t('toast.success.generationSuccess'), {
          description: `${images.length} Bild(er) wurden erstellt`,
        });

        return images;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        toast.error(t('common.error'), {
          description: errorMessage,
        });
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const generateInpaintAdd = useCallback(
    (params: GenerateParams) =>
      generateRequest('/api/v2/image-edit/inpaint/add', params),
    [generateRequest]
  );

  const generateInpaintRemove = useCallback(
    (params: GenerateParams) =>
      generateRequest('/api/v2/image-edit/inpaint/remove', params),
    [generateRequest]
  );

  const generateOutpaint = useCallback(
    (params: GenerateParams) =>
      generateRequest('/api/v2/image-edit/outpaint', params),
    [generateRequest]
  );

  const generateBackgroundEdit = useCallback(
    (params: GenerateParams) =>
      generateRequest('/api/v2/image-edit/background-edit', params),
    [generateRequest]
  );

  return {
    generateInpaintAdd,
    generateInpaintRemove,
    generateOutpaint,
    generateBackgroundEdit,
    isLoading,
    error,
  };
}
